import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaBox, FaCalendarAlt, FaChevronDown, FaChevronRight, FaChevronUp, FaClipboardList, FaCog, FaExternalLinkAlt, FaFileAlt, FaFolder, FaGripVertical, FaHistory, FaInfoCircle, FaKey, FaListAlt, FaMapMarkerAlt, FaMinusCircle, FaPen, FaPlusCircle, FaSyncAlt, FaTag, FaTrash } from "react-icons/fa";
import { getPhysicalObject, getPhysicalObjectAudit, getPhysicalObjectContents, getPhysicalRecordsClassifications, movePhysicalFileToBox, removePhysicalFileFromBox, searchPhysicalObjects, setPhysicalObjectClassification, updatePhysicalObjectKeyInformation, updatePhysicalObjectPhysicalDetails, updatePhysicalObjectRecordsDetail, type PhysicalObjectAuditEvent, type PhysicalObjectDetail as PhysicalObject, type PhysicalObjectSearch, type PhysicalRecordsClassification } from "@/api/physicalRecordsApi";
import { usePageTitle } from "@/hooks/usePageTitle";
import { PhysicalObjectCategories } from "@/components/PhysicalRecords/PhysicalRecordCategories";

const PAGE_SIZE = 50;
const COMPACT_CONTENT_WIDTH = 1080;
const WORKSPACE_WIDTH_STORAGE_KEY = "physical-record-detail-workspace-width-v2";
const WORKSPACE_COLLAPSED_STORAGE_KEY = "physical-record-detail-workspace-collapsed";

type DetailView = "details" | "audit" | "migration" | "contents" | "categories";
type EditSection = "key-information" | "physical-details" | "classification" | "records-detail";
type RelationshipMode = "move-file" | "add-file";

const LEGACY_EVENT_LABELS: Record<string, string> = {
  Create: "Created",
  PermChange: "Permissions Changed",
  AttrChange: "Attribute Value Changed",
  POLocationChanged: "Location Changed",
  POAssignToBox: "Assigned to Box",
  PORemoveFromBox: "Removed from Box",
  RMClassificationAdded: "Classification Added",
  RMClassificationRemoved: "Classification Removed",
  RMClassificationChanged: "Classification Changed",
  RSIChanged: "RSI Changed",
  StatusChanged: "Status Changed",
  CyclePeriodReviewChanged: "Cycle Period Review Changed",
};

const AUDIT_EVENT_STYLES: Record<string, { icon: typeof FaHistory; tone: string }> = {
  Create: { icon: FaPlusCircle, tone: "creation" },
  PermChange: { icon: FaKey, tone: "permissions" },
  AttrChange: { icon: FaCog, tone: "metadata" },
  POLocationChanged: { icon: FaMapMarkerAlt, tone: "location" },
  POAssignToBox: { icon: FaBox, tone: "location" },
  PORemoveFromBox: { icon: FaMinusCircle, tone: "removal" },
  RMClassificationAdded: { icon: FaTag, tone: "classification" },
  RMClassificationRemoved: { icon: FaMinusCircle, tone: "removal" },
  RMClassificationChanged: { icon: FaTag, tone: "classification" },
  RSIChanged: { icon: FaFileAlt, tone: "retention" },
  StatusChanged: { icon: FaClipboardList, tone: "metadata" },
  CyclePeriodReviewChanged: { icon: FaSyncAlt, tone: "review" },
};

function AuditEventLabel({ event }: { event: PhysicalObjectAuditEvent }) {
  const Icon = (AUDIT_EVENT_STYLES[event.eventCode] ?? { icon: FaHistory }).icon;
  return <span className="physical-audit-event-label"><Icon aria-hidden="true" /><span>{LEGACY_EVENT_LABELS[event.eventCode] ?? event.eventLabel ?? event.eventCode}</span></span>;
}

function formatDateTime(value?: string | null) {
  return value ? new Date(value).toLocaleString() : "—";
}

function formatDate(value?: string | null) {
  return value ? new Date(`${value.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
}

function PageControls({ page, pageSize, total, onChange }: { page: number; pageSize: number; total: number; onChange: (page: number) => void }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  return <div className="d-flex align-items-center justify-content-between py-2">
    <span className="small text-muted">{total.toLocaleString()} items · Page {page} of {pageCount}</span>
    <div className="btn-group btn-group-sm" role="group" aria-label="Pagination">
      <button type="button" className="btn btn-outline-secondary" disabled={page <= 1} onClick={() => onChange(page - 1)}>Previous</button>
      <button type="button" className="btn btn-outline-secondary" disabled={page >= pageCount} onClick={() => onChange(page + 1)}>Next</button>
    </div>
  </div>;
}

export default function PhysicalObjectDetail() {
  const { physicalObjectId } = useParams<{ physicalObjectId: string }>();
  const [object, setObject] = useState<PhysicalObject | null>(null);
  const [contents, setContents] = useState<PhysicalObjectSearch[]>([]);
  const [contentsPage, setContentsPage] = useState(1);
  const [contentsTotal, setContentsTotal] = useState(0);
  const [contentsText, setContentsText] = useState("");
  const [appliedContentsText, setAppliedContentsText] = useState("");
  const [contentsSort, setContentsSort] = useState("physicalId");
  const [contentsLoading, setContentsLoading] = useState(false);
  const [contentsError, setContentsError] = useState<string | null>(null);
  const [audit, setAudit] = useState<PhysicalObjectAuditEvent[]>([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [expandedAuditIds, setExpandedAuditIds] = useState<Set<number>>(new Set());
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editSection, setEditSection] = useState<EditSection | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [editError, setEditError] = useState<string | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [classificationOptions, setClassificationOptions] = useState<PhysicalRecordsClassification[]>([]);
  const [classificationLoading, setClassificationLoading] = useState(false);
  const [relationshipMode, setRelationshipMode] = useState<RelationshipMode | null>(null);
  const [relationshipSearch, setRelationshipSearch] = useState("");
  const [relationshipResults, setRelationshipResults] = useState<PhysicalObjectSearch[]>([]);
  const [relationshipSelection, setRelationshipSelection] = useState<number | null>(null);
  const [relationshipLoading, setRelationshipLoading] = useState(false);
  const [savingRelationship, setSavingRelationship] = useState(false);
  const [relationshipError, setRelationshipError] = useState<string | null>(null);
  const [compact, setCompact] = useState(false);
  const [workspaceMaxWidth, setWorkspaceMaxWidth] = useState(560);
  const [workspaceWidth, setWorkspaceWidth] = useState(() => {
    const stored = Number(window.localStorage.getItem(WORKSPACE_WIDTH_STORAGE_KEY));
    return Number.isFinite(stored) && stored >= 300 ? stored : 0;
  });
  const [workspaceCollapsed, setWorkspaceCollapsed] = useState(() => window.localStorage.getItem(WORKSPACE_COLLAPSED_STORAGE_KEY) === "true");
  const [workspaceResizing, setWorkspaceResizing] = useState(false);
  const detailContainerRef = useRef<HTMLDivElement | null>(null);
  const resizeStartRef = useRef<{ pointerX: number; width: number } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? "details";

  usePageTitle(object?.name, "Physical Object Details");

  useEffect(() => {
    let active = true;
    const reference = physicalObjectId?.trim() ?? "";
    const id = /^[BF][0-9]{1,63}$/i.test(reference) ? reference.toUpperCase() : Number(reference);
    setObject(null);
    setError(null);
    setAudit([]);
    setAuditPage(1);
    setContents([]);
    setContentsPage(1);
    if (typeof id === "number" && (!/^\d+$/.test(reference) || !Number.isSafeInteger(id) || id <= 0)) {
      setError("Enter a numeric record ID or a physical ID starting with B or F followed by digits.");
      return;
    }
    getPhysicalObject(id)
      .then((detail) => { if (active) setObject(detail); })
      .catch((e) => { if (active) setError(e?.response?.data?.error ?? e?.message ?? "Failed to load object."); });
    return () => { active = false; };
  }, [physicalObjectId]);

  useEffect(() => {
    const element = detailContainerRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      const contentWidth = entry.contentRect.width;
      setCompact(contentWidth < COMPACT_CONTENT_WIDTH);
      const maxWidth = Math.max(300, contentWidth / 2);
      setWorkspaceMaxWidth(maxWidth);
      if (contentWidth >= COMPACT_CONTENT_WIDTH) {
        setWorkspaceWidth((width) => width === 0 ? maxWidth : Math.min(width, maxWidth));
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [object?.id]);

  useEffect(() => {
    if (object?.objectType !== "File" || activeTab !== "contents") return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", "details");
    setSearchParams(nextParams, { replace: true });
  }, [object?.objectType, activeTab, searchParams, setSearchParams]);

  useEffect(() => {
    if (editSection !== "classification") return;
    setClassificationLoading(true);
    getPhysicalRecordsClassifications()
      .then(setClassificationOptions)
      .catch((e) => setEditError(e?.response?.data?.error ?? e?.message ?? "Failed to load classifications."))
      .finally(() => setClassificationLoading(false));
  }, [editSection]);

  useEffect(() => {
    try {
      window.localStorage.setItem(WORKSPACE_WIDTH_STORAGE_KEY, String(workspaceWidth));
      window.localStorage.setItem(WORKSPACE_COLLAPSED_STORAGE_KEY, String(workspaceCollapsed));
    } catch {
      // Storage may be unavailable in restricted browser contexts.
    }
  }, [workspaceWidth, workspaceCollapsed]);

  useEffect(() => {
    if (!workspaceResizing) return;
    const onPointerMove = (event: PointerEvent) => {
      const start = resizeStartRef.current;
      if (!start) return;
      setWorkspaceWidth(Math.min(workspaceMaxWidth, Math.max(300, start.width + start.pointerX - event.clientX)));
    };
    const onPointerUp = () => {
      resizeStartRef.current = null;
      setWorkspaceResizing(false);
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [workspaceResizing, workspaceMaxWidth]);

  useEffect(() => {
    const id = object?.id;
    const workspaceView = activeTab === "details" ? "audit" : activeTab;
    if (!id || object?.objectType !== "Box" || (compact ? activeTab !== "contents" : workspaceView !== "contents")) return;
    setContentsLoading(true);
    setContentsError(null);
    getPhysicalObjectContents(id, { page: contentsPage, pageSize: PAGE_SIZE, text: appliedContentsText.trim() || undefined, sort: contentsSort })
      .then((result) => { setContents(result.items); setContentsTotal(result.totalCount); })
      .catch((e) => setContentsError(e?.response?.data?.error ?? e?.message ?? "Failed to load Box contents."))
      .finally(() => setContentsLoading(false));
  }, [object?.id, activeTab, compact, object?.objectType, contentsPage, appliedContentsText, contentsSort]);

  useEffect(() => {
    const id = object?.id;
    const workspaceView = activeTab === "details" ? "audit" : activeTab;
    if (!id || (compact ? activeTab !== "audit" : workspaceView !== "audit")) return;
    setAuditLoading(true);
    setAuditError(null);
    getPhysicalObjectAudit(id, { page: auditPage, pageSize: PAGE_SIZE })
      .then((result) => { setAudit(result.items); setAuditTotal(result.totalCount); })
      .catch((e) => setAuditError(e?.response?.data?.error ?? e?.message ?? "Failed to load audit history."))
      .finally(() => setAuditLoading(false));
  }, [object?.id, activeTab, compact, auditPage]);

  function selectTab(tab: DetailView) {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", tab);
    setSearchParams(nextParams, { replace: true });
  }

  function openEditor(section: EditSection) {
    if (!object) return;
    setEditError(null);
    setEditSection(section);
    if (section === "key-information") {
      setEditValues({ name: object.name, description: object.description ?? "" });
    } else if (section === "physical-details") {
      setEditValues({
        homeLocationRaw: object.homeLocationRaw ?? "", currentLocationRaw: object.currentLocationRaw ?? "",
        keywords: object.keywords ?? "", contentStartDate: object.contentStartDate ?? "", contentEndDate: object.contentEndDate ?? "",
        contractParties: object.contractParties ?? "",
        offsiteId: object.offsiteId ?? "", offsiteStorageId: object.offsiteStorageId ?? "",
      });
    } else if (section === "classification") {
      setEditValues({ classificationId: object.classificationId == null ? "" : String(object.classificationId) });
    } else {
      setEditValues({ recordDate: object.recordDate ?? "", recordStatus: object.recordStatus ?? "", statusDate: object.statusDate ?? "", nextReviewDate: object.nextReviewDate ?? "" });
    }
  }

  function closeEditor() {
    if (savingEdit) return;
    setEditSection(null);
    setEditError(null);
  }

  async function refreshObject() {
    if (object) setObject(await getPhysicalObject(object.id));
  }

  async function saveEditor(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!object || !editSection) return;
    setSavingEdit(true);
    setEditError(null);
    const blankToNull = (value: string | undefined) => value?.trim() ? value : null;
    try {
      if (editSection === "key-information") {
        await updatePhysicalObjectKeyInformation(object.id, { name: editValues.name ?? "", description: blankToNull(editValues.description) });
      } else if (editSection === "physical-details") {
        await updatePhysicalObjectPhysicalDetails(object.id, {
          homeLocationRaw: blankToNull(editValues.homeLocationRaw), currentLocationRaw: blankToNull(editValues.currentLocationRaw),
          keywords: blankToNull(editValues.keywords), contentStartDate: blankToNull(editValues.contentStartDate), contentEndDate: blankToNull(editValues.contentEndDate),
          contractParties: blankToNull(editValues.contractParties),
          offsiteId: blankToNull(editValues.offsiteId), offsiteStorageId: blankToNull(editValues.offsiteStorageId),
        });
      } else if (editSection === "classification") {
        const rawId = editValues.classificationId ?? "";
        await setPhysicalObjectClassification(object.id, rawId ? Number(rawId) : null);
      } else {
        await updatePhysicalObjectRecordsDetail(object.id, {
          recordDate: blankToNull(editValues.recordDate), recordStatus: blankToNull(editValues.recordStatus),
          statusDate: blankToNull(editValues.statusDate), nextReviewDate: blankToNull(editValues.nextReviewDate),
        });
      }
      await refreshObject();
      setEditSection(null);
    } catch (e: any) {
      setEditError(e?.response?.data?.error ?? e?.message ?? "Unable to save changes.");
    } finally {
      setSavingEdit(false);
    }
  }

  function openRelationship(mode: RelationshipMode) {
    setRelationshipMode(mode);
    setRelationshipSearch("");
    setRelationshipSelection(null);
    setRelationshipResults([]);
    setRelationshipError(null);
  }

  async function runRelationshipSearch() {
    if (!relationshipMode || relationshipSearch.trim().length < 2) {
      setRelationshipError("Enter at least two characters to search.");
      return;
    }
    setRelationshipLoading(true);
    setRelationshipError(null);
    setRelationshipSelection(null);
    try {
      const result = await searchPhysicalObjects({
        text: relationshipSearch.trim(),
        objectType: relationshipMode === "move-file" ? "Box" : "File",
        page: 1,
        pageSize: 50,
      });
      setRelationshipResults(result.items.filter((item) => item.id !== object?.id));
    } catch (e: any) {
      setRelationshipError(e?.response?.data?.error ?? e?.message ?? "Failed to search Physical Records.");
    } finally {
      setRelationshipLoading(false);
    }
  }

  async function saveRelationship() {
    if (!object || !relationshipMode || relationshipSelection == null) return;
    const fileId = relationshipMode === "add-file" ? relationshipSelection : object.id;
    const boxId = relationshipMode === "add-file" ? object.id : relationshipSelection;
    setSavingRelationship(true);
    setRelationshipError(null);
    try {
      await movePhysicalFileToBox(fileId, boxId);
      await refreshObject();
      if (object.objectType === "Box") {
        const result = await getPhysicalObjectContents(object.id, { page: contentsPage, pageSize: PAGE_SIZE, text: appliedContentsText || undefined, sort: contentsSort });
        setContents(result.items);
        setContentsTotal(result.totalCount);
      }
      setRelationshipMode(null);
    } catch (e: any) {
      setRelationshipError(e?.response?.data?.error ?? e?.message ?? "Unable to update the Box relationship.");
    } finally {
      setSavingRelationship(false);
    }
  }

  async function removeFileFromCurrentBox(fileId: number) {
    if (!object || !window.confirm("Remove this File from the Box? The File itself will remain.")) return;
    try {
      await removePhysicalFileFromBox(fileId);
      const result = await getPhysicalObjectContents(object.id, { page: contentsPage, pageSize: PAGE_SIZE, text: appliedContentsText || undefined, sort: contentsSort });
      setContents(result.items);
      setContentsTotal(result.totalCount);
      await refreshObject();
    } catch (e: any) {
      setContentsError(e?.response?.data?.error ?? e?.message ?? "Unable to remove the File from this Box.");
    }
  }

  function beginWorkspaceResize(event: React.PointerEvent<HTMLButtonElement>) {
    resizeStartRef.current = { pointerX: event.clientX, width: workspaceWidth };
    setWorkspaceResizing(true);
    event.preventDefault();
  }

  function searchContents() {
    setAppliedContentsText(contentsText);
    setContentsPage(1);
  }

  function toggleAuditDetails(eventId: number) {
    setExpandedAuditIds((current) => {
      const next = new Set(current);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  }

  const numberLabel = object?.objectType === "Box" ? "Box Number" : "File Number";
  const fileParent = object?.objectType === "File" && object.parentPhysicalObjectId != null;
  const availableViews: DetailView[] = object?.objectType === "Box"
    ? ["details", "categories", "audit", "migration", "contents"]
    : ["details", "categories", "audit", "migration"];
  const selectedView: DetailView = availableViews.includes(activeTab as DetailView) ? activeTab as DetailView : "details";
  const workspaceView: Exclude<DetailView, "details"> = selectedView === "details" ? "audit" : selectedView;

  if (error) return <div className="container-fluid px-3 px-lg-4 py-3"><Alert variant="danger">{error}</Alert></div>;
  if (!object) return <div className="container-fluid px-3 px-lg-4 py-3">Loading...</div>;

  const detailContent = <div className="physical-object-details-grid">
    <section className="card physical-object-detail-card physical-object-key-card">
      <div className="card-body">
        <div className="physical-object-card-heading"><h2><FaInfoCircle aria-hidden="true" />Key Information</h2><Button size="sm" variant="outline-primary" onClick={() => openEditor("key-information")}><FaPen aria-hidden="true" /> Edit</Button></div>
        <dl className="physical-object-fields">
          <dt>Name</dt><dd>{object.name}</dd>
          <dt>{numberLabel}</dt><dd>{object.physicalId ?? "—"}</dd>
          <dt>Object type</dt><dd>{object.objectType}</dd>
          <dt>Description</dt><dd className="physical-object-description">{object.description ?? "—"}</dd>
          <dt>Directory path</dt><dd className="physical-object-directory-path"><FaFolder aria-hidden="true" /><span>{object.directoryPath ?? object.directoryName ?? "—"}</span></dd>
          <dt>Created</dt><dd className="physical-object-date-value"><FaCalendarAlt aria-hidden="true" /><span>{formatDateTime(object.sourceCreatedDate)}{object.sourceCreatedBy ? ` · ${object.sourceCreatedBy}` : ""}</span></dd>
          <dt>Modified</dt><dd className="physical-object-date-value"><FaCalendarAlt aria-hidden="true" /><span>{formatDateTime(object.sourceModifiedDate)}{object.sourceModifiedBy ? ` · ${object.sourceModifiedBy}` : ""}</span></dd>
        </dl>
        {fileParent && <div className="physical-object-relationship">
          <span><FaBox aria-hidden="true" /> Containing Box</span>
          <Link className="physical-object-box-link" to={`/physical-records/${object.parentPhysicalObjectId}`}>{object.parentPhysicalId ?? object.parentName ?? object.parentPhysicalObjectId}<FaExternalLinkAlt aria-hidden="true" /></Link>
          <div className="d-flex gap-2"><Button size="sm" variant="outline-primary" onClick={() => openRelationship("move-file")}><FaFolder aria-hidden="true" /> Move</Button><Button size="sm" variant="outline-danger" onClick={() => void removeFileFromCurrentBox(object.id)}><FaTrash aria-hidden="true" /> Remove</Button></div>
        </div>}
        {object.objectType === "File" && !fileParent && <div className="physical-object-relationship"><span>Containing Box</span><span className="text-muted">Not in a Box</span><Button size="sm" variant="outline-primary" onClick={() => openRelationship("move-file")}>Add to Box</Button></div>}
      </div>
    </section>

    <section className="card physical-object-detail-card physical-object-classification-card">
      <div className="card-body">
        <div className="physical-object-card-heading"><h2><FaTag aria-hidden="true" />Classification</h2><Button size="sm" variant="outline-primary" onClick={() => openEditor("classification")}><FaPen aria-hidden="true" /> Change Classification</Button></div>
        {object.classificationId == null ? <p className="mb-0 text-muted">Unclassified</p> : <>
        <div className="physical-object-classification-identity">{object.classificationCode && <span className="physical-object-identity-chip"><FaTag aria-hidden="true" />{object.classificationCode}</span>}<p>{object.classificationName ?? "—"}</p></div>
        <dl className="physical-object-fields">
          <dt>RSI</dt><dd>{object.classificationRsi ?? "—"}</dd>
          <dt>File status</dt><dd>{object.classificationFileStatus ?? "—"}</dd>
          <dt>Essential</dt><dd>{object.classificationEssential ?? "—"}</dd>
          <dt>Storage Medium</dt><dd>{object.classificationStorage ?? "—"}</dd>
          <dt>Cycle Period</dt><dd>{object.classificationCyclePeriod ?? "—"}</dd>
        </dl></>}
      </div>
    </section>

    <section className="card physical-object-detail-card physical-object-records-card">
      <div className="card-body">
        <div className="physical-object-card-heading"><h2><FaClipboardList aria-hidden="true" />Records Detail</h2><Button size="sm" variant="outline-primary" onClick={() => openEditor("records-detail")}>Edit</Button></div>
        <dl className="physical-object-fields">
          <dt>Record Status</dt><dd>{object.recordStatus ? <span className="physical-record-status-badge">{object.recordStatus}</span> : "—"}</dd>
          <dt>Record Date</dt><dd>{formatDate(object.recordDate)}</dd>
          <dt>Status Date</dt><dd>{formatDate(object.statusDate)}</dd>
          <dt>Next Review Date</dt><dd>{formatDate(object.nextReviewDate)}</dd>
        </dl>
      </div>
    </section>

    <section className="card physical-object-detail-card physical-object-physical-card">
      <div className="card-body">
        <div className="physical-object-card-heading"><h2><FaMapMarkerAlt aria-hidden="true" />Physical Details</h2><Button size="sm" variant="outline-primary" onClick={() => openEditor("physical-details")}>Edit</Button></div>
        <dl className="physical-object-fields">
          <dt>Home location</dt><dd>{object.homeLocationRaw ?? "—"}</dd>
          <dt>Current location</dt><dd>{object.currentLocationRaw ?? "—"}</dd>
          <dt>Content from</dt><dd>{formatDate(object.contentStartDate)}</dd>
          <dt>Content to</dt><dd>{formatDate(object.contentEndDate)}</dd>
          <dt>Keywords</dt><dd>{object.keywords ?? "—"}</dd>
          <dt>Last transfer user</dt><dd>{object.lastTransferUser ?? "—"}</dd>
          <dt>Last transfer date</dt><dd>{formatDate(object.lastTransferDate)}</dd>
          <dt>Contract parties</dt><dd>{object.contractParties ?? "—"}</dd>
          <dt>File ID</dt><dd>{object.legacyFileId ?? "—"}</dd>
          <dt>Offsite ID</dt><dd>{object.offsiteId ?? "—"}</dd>
          <dt>Offsite storage ID</dt><dd>{object.offsiteStorageId ?? "—"}</dd>
        </dl>
      </div>
    </section>
  </div>;

  const auditContent = <div className="physical-workspace-view">
    <div className="physical-workspace-heading"><h2><FaHistory aria-hidden="true" />Audit History <span className="physical-workspace-event-count">{auditTotal.toLocaleString()}</span></h2></div>
    {auditError && <Alert variant="danger">{auditError}</Alert>}
    {auditLoading ? <div className="py-4 text-center"><Spinner size="sm" className="me-2" />Loading audit history...</div> : <div className="table-responsive"><table className="table table-sm table-hover align-middle mb-0 physical-object-audit-table"><thead><tr><th>Event</th><th>Date/time</th><th>User / performer</th><th aria-label="Details" /></tr></thead><tbody>{audit.map((event) => <Fragment key={event.auditEventId}>
      <tr className={`physical-audit-event-row audit-tone-${AUDIT_EVENT_STYLES[event.eventCode]?.tone ?? "neutral"}`}><td><AuditEventLabel event={event} /></td><td>{formatDateTime(event.eventDate)}</td><td>{event.performerDisplayName ?? event.performerRaw ?? (event.legacyPerformerId == null ? "—" : String(event.legacyPerformerId))}</td><td><button type="button" className="physical-audit-details-toggle" aria-label={`${expandedAuditIds.has(event.auditEventId) ? "Hide" : "Show"} details for ${LEGACY_EVENT_LABELS[event.eventCode] ?? event.eventLabel ?? event.eventCode}`} aria-expanded={expandedAuditIds.has(event.auditEventId)} aria-controls={`audit-detail-${event.auditEventId}`} onClick={() => toggleAuditDetails(event.auditEventId)}>{expandedAuditIds.has(event.auditEventId) ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}<span>Details</span></button></td></tr>
      {expandedAuditIds.has(event.auditEventId) && <tr><td colSpan={4}><dl className="row mb-0 small"><dt className="col-sm-3">Performer raw</dt><dd className="col-sm-9">{event.performerRaw ?? "—"}</dd><dt className="col-sm-3">Legacy performer ID</dt><dd className="col-sm-9">{event.legacyPerformerId ?? "—"}</dd><dt className="col-sm-3">Supplemental detail key</dt><dd className="col-sm-9">{event.detailKey ?? "—"}</dd><dt className="col-sm-3">Supplemental detail value</dt><dd className="col-sm-9 text-break">{event.detailValue ?? "—"}</dd><dt className="col-sm-3">Legacy value key</dt><dd className="col-sm-9">{event.legacyValueKey ?? "—"}</dd><dt className="col-sm-3">Legacy value 1</dt><dd className="col-sm-9 text-break">{event.legacyValue1 ?? "—"}</dd><dt className="col-sm-3">Legacy value 2</dt><dd className="col-sm-9 text-break">{event.legacyValue2 ?? "—"}</dd></dl></td></tr>}
    </Fragment>)}{audit.length === 0 && <tr><td colSpan={4} className="text-center text-muted py-4">No audit history is available for this record yet.</td></tr>}</tbody></table></div>}
    <PageControls page={auditPage} pageSize={PAGE_SIZE} total={auditTotal} onChange={setAuditPage} />
  </div>;

  const migrationContent = <div className="physical-workspace-view"><div className="physical-workspace-heading"><h2>Migration Info</h2><span>Read only</span></div><dl className="physical-object-fields">
    <dt>Source system</dt><dd>{object.legacySystem ?? "—"}</dd><dt>Legacy node</dt><dd>{object.legacyNodeId ?? "—"}</dd><dt>Legacy parent</dt><dd>{object.legacyParentNodeId ?? "—"}</dd>
    <dt>Legacy subtype / media</dt><dd>{object.legacySubtype ?? "—"} / {object.legacyMediaTypeId ?? "—"}</dd><dt>Legacy deleted code</dt><dd>{object.legacyDeletedCode ?? "—"}{object.legacyDeletedCodeMeaningUnvalidated && <span className="text-warning ms-2">Source status meaning not validated</span>}</dd>
    <dt>Legacy container</dt><dd>{object.legacyContainerNumber ?? "—"}</dd>
  </dl></div>;

  const contentsContent = <div className="physical-workspace-view">
    <div className="physical-workspace-heading"><h2>Box Contents ({object.childCount})</h2><Button size="sm" variant="primary" onClick={() => openRelationship("add-file")}>Add File</Button></div>
    <form className="d-flex gap-2 mb-3" onSubmit={(event) => { event.preventDefault(); searchContents(); }}>
      <label className="visually-hidden" htmlFor="boxContentsSearch">Search Box contents</label><input id="boxContentsSearch" className="form-control form-control-sm" value={contentsText} onChange={(event) => setContentsText(event.target.value)} placeholder="Search files..." />
      <Button type="submit" size="sm">Search</Button><Button type="button" size="sm" variant="outline-secondary" onClick={() => { setContentsText(""); setAppliedContentsText(""); setContentsPage(1); }}>Clear</Button>
      <Form.Select aria-label="Sort Box contents" size="sm" className="w-auto" value={contentsSort} onChange={(event) => { setContentsSort(event.target.value); setContentsPage(1); }}><option value="physicalId">File Number</option><option value="name">Name</option><option value="contentStartDate">Content From</option><option value="contentEndDate">Content To</option><option value="description">Description</option></Form.Select>
    </form>
    {contentsError && <Alert variant="danger">{contentsError}</Alert>}
    {contentsLoading ? <div className="py-4 text-center"><Spinner size="sm" className="me-2" />Loading Box contents...</div> : <div className="table-responsive"><table className="table table-sm table-hover align-middle mb-0"><thead><tr><th>File Number</th><th>Name</th><th>Description</th><th>Content From</th><th>Content To</th><th>Action</th></tr></thead><tbody>{contents.map((file) => <tr key={file.id}><td><Link to={`/physical-records/${file.id}`}>{file.physicalId ?? "—"}</Link></td><td>{file.name}</td><td>{file.description ?? "—"}</td><td>{file.contentStartDate ?? "—"}</td><td>{file.contentEndDate ?? "—"}</td><td><Button size="sm" variant="outline-danger" onClick={() => void removeFileFromCurrentBox(file.id)}>Remove</Button></td></tr>)}{contents.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-4">No contained files found.</td></tr>}</tbody></table></div>}
    <PageControls page={contentsPage} pageSize={PAGE_SIZE} total={contentsTotal} onChange={setContentsPage} />
  </div>;

  const viewContent = (view: Exclude<DetailView, "details">) => view === "categories" ? <PhysicalObjectCategories key={object.id} objectId={object.id} /> : view === "audit" ? auditContent : view === "migration" ? migrationContent : contentsContent;
  const selectWorkspaceView = (view: DetailView) => selectTab(view);
  const workspaceTabButtons = (mobile: boolean) => <nav className={mobile ? "physical-object-mobile-tabs" : "physical-object-secondary-tabs"} aria-label={mobile ? "Physical Record views" : "Related information"}>
    {availableViews.filter((view) => mobile || view !== "details").map((view) => <button key={view} type="button" aria-current={(mobile ? selectedView : workspaceView) === view ? "page" : undefined} className={(mobile ? selectedView : workspaceView) === view ? "active" : ""} onClick={() => selectWorkspaceView(view)}>{view === "details" ? <><FaListAlt aria-hidden="true" /><span>Details</span></> : view === "categories" ? <><FaTag aria-hidden="true" /><span>Categories</span></> : view === "audit" ? <><FaHistory aria-hidden="true" /><span>Audit</span></> : view === "migration" ? <><FaInfoCircle aria-hidden="true" /><span>{mobile ? "Migration Info" : "Migration"}</span><span className="physical-tab-note">Read only</span></> : <><FaClipboardList aria-hidden="true" /><span>Box Contents</span><span className="physical-tab-count">{object.childCount}</span></>}</button>)}
  </nav>;

  function editField(label: string, key: string, type: string = "text", options?: { as?: "textarea"; maxLength?: number }) {
    return <Form.Group className="mb-3" key={key} controlId={`edit-${key}`}><Form.Label>{label}</Form.Label><Form.Control type={type} as={options?.as} maxLength={options?.maxLength} value={editValues[key] ?? ""} onChange={(event) => setEditValues((current) => ({ ...current, [key]: event.target.value }))} /></Form.Group>;
  }

  const editorTitle = editSection === "key-information" ? "Edit Key Information" : editSection === "physical-details" ? "Edit Physical Details" : editSection === "classification" ? "Change Classification" : "Edit Records Detail";
  const rightWidthStyle = { "--detail-workspace-width": `${workspaceWidth || workspaceMaxWidth}px` } as React.CSSProperties;

  return <div className={`container-fluid px-3 px-lg-4 py-3 physical-object-detail-page ${compact ? "is-compact" : ""}`} ref={detailContainerRef}>
    <div className="physical-object-detail-header"><div><div className="physical-object-back-link"><Link to="/physical-records/browse">&larr; Back to browser</Link></div><h1>{object.name}</h1><div className="physical-object-header-meta"><span className="physical-object-identity-chip">{object.objectType === "Box" ? <FaBox aria-hidden="true" /> : <FaFileAlt aria-hidden="true" />}{object.objectType}</span><span className="physical-object-identity-chip">{numberLabel}: <strong>{object.physicalId ?? "—"}</strong></span>{object.classificationCode && <span className="physical-object-identity-chip" title={object.classificationName ?? "Classification"}><FaTag aria-hidden="true" /> {object.classificationCode}</span>}</div></div></div>

    {compact && workspaceTabButtons(true)}
    <div className={`physical-object-detail-workspace ${compact ? "is-compact" : ""} ${workspaceCollapsed && !compact ? "workspace-collapsed" : ""} ${workspaceResizing ? "workspace-resizing" : ""}`} style={rightWidthStyle}>
      {(!compact || selectedView === "details") && <main className="physical-object-details-pane" aria-label="Physical Record details">{detailContent}</main>}
      {!compact && !workspaceCollapsed && <button type="button" className="physical-object-workspace-resizer" aria-label="Resize related information panel; drag or use arrow keys" title="Drag to resize" data-tooltip="Drag to resize" onPointerDown={beginWorkspaceResize} onKeyDown={(event) => { if (event.key === "ArrowLeft") { event.preventDefault(); setWorkspaceWidth((width) => Math.min(workspaceMaxWidth, width + 20)); } if (event.key === "ArrowRight") { event.preventDefault(); setWorkspaceWidth((width) => Math.max(300, width - 20)); } }}><FaGripVertical aria-hidden="true" /></button>}
      {!compact && workspaceCollapsed && <aside className="physical-object-workspace-rail"><Button size="sm" variant="outline-secondary" aria-label="Expand related information" title="Expand related information" onClick={() => setWorkspaceCollapsed(false)}>‹</Button>{availableViews.filter((view) => view !== "details").map((view) => <button key={view} type="button" className={workspaceView === view ? "active" : ""} title={view === "categories" ? "Categories" : view === "audit" ? "Audit" : view === "migration" ? "Migration Info" : "Box Contents"} onClick={() => selectWorkspaceView(view)}>{view === "categories" ? "Categories" : view === "audit" ? "Audit" : view === "migration" ? "Migration" : "Contents"}</button>)}</aside>}
      {(compact ? selectedView !== "details" : !workspaceCollapsed) && <aside className="physical-object-secondary-pane" aria-label="Related information">
        {!compact && <div className="physical-object-secondary-header">{workspaceTabButtons(false)}<Button size="sm" variant="outline-secondary" aria-label="Collapse related information" title="Collapse related information" onClick={() => setWorkspaceCollapsed(true)}><FaChevronRight aria-hidden="true" /></Button></div>}
        <div className="physical-object-secondary-content">{viewContent(workspaceView)}</div>
      </aside>}
    </div>

    <Modal show={editSection !== null} onHide={closeEditor} centered size="lg">
      <Form onSubmit={(event) => void saveEditor(event)}>
        <Modal.Header closeButton><Modal.Title>{editorTitle}</Modal.Title></Modal.Header>
        <Modal.Body>
          {editError && <Alert variant="danger">{editError}</Alert>}
          {editSection === "key-information" && <>{editField("Name", "name", "text", { maxLength: 512 })}{editField("Description", "description", "text", { as: "textarea", maxLength: 2048 })}</>}
          {editSection === "physical-details" && <>
            {editField("Home location", "homeLocationRaw", "text", { maxLength: 512 })}{editField("Current location", "currentLocationRaw", "text", { maxLength: 512 })}
            {editField("Content from", "contentStartDate", "date")}{editField("Content to", "contentEndDate", "date")}
            {editField("Keywords", "keywords", "text", { as: "textarea" })}{editField("Contract parties", "contractParties", "text", { maxLength: 1024 })}
            {editField("Offsite ID", "offsiteId", "text", { maxLength: 256 })}{editField("Offsite storage ID", "offsiteStorageId", "text", { maxLength: 256 })}
            <p className="small text-muted">Last transfer user and date are system-maintained.</p>
          </>}
          {editSection === "classification" && <Form.Group controlId="edit-classification"><Form.Label>Classification</Form.Label><Form.Select value={editValues.classificationId ?? ""} disabled={classificationLoading} onChange={(event) => setEditValues((current) => ({ ...current, classificationId: event.target.value }))}><option value="">Unclassified</option>{classificationOptions.map((item) => <option key={item.classificationId} value={item.classificationId}>{item.code} — {item.name}</option>)}</Form.Select>{classificationLoading && <Form.Text>Loading classification catalog...</Form.Text>}</Form.Group>}
          {editSection === "records-detail" && <>{editField("Record Status", "recordStatus", "text", { maxLength: 128 })}{editField("Record Date", "recordDate", "date")}{editField("Status Date", "statusDate", "date")}{editField("Next Review Date", "nextReviewDate", "date")}</>}
        </Modal.Body>
        <Modal.Footer><Button variant="outline-secondary" onClick={closeEditor} disabled={savingEdit}>Cancel</Button><Button type="submit" variant="primary" disabled={savingEdit || (editSection === "classification" && classificationLoading)}>{savingEdit ? "Saving..." : "Save changes"}</Button></Modal.Footer>
      </Form>
    </Modal>

    <Modal show={relationshipMode !== null} onHide={() => !savingRelationship && setRelationshipMode(null)} centered size="lg">
      <Modal.Header closeButton><Modal.Title>{relationshipMode === "add-file" ? "Add File to Box" : "Move File to Box"}</Modal.Title></Modal.Header>
      <Modal.Body>
        {relationshipError && <Alert variant="danger">{relationshipError}</Alert>}
        <p>{relationshipMode === "add-file" ? <>Choose an existing File to add to <strong>{object.name}</strong>.</> : <>Choose the destination Box for <strong>{object.name}</strong>.</>}</p>
        <Form onSubmit={(event) => { event.preventDefault(); void runRelationshipSearch(); }}>
          <Form.Group controlId="relationshipSearch"><Form.Label>{relationshipMode === "add-file" ? "Search Files" : "Search Boxes"}</Form.Label><div className="d-flex gap-2"><Form.Control value={relationshipSearch} onChange={(event) => setRelationshipSearch(event.target.value)} placeholder="Enter at least two characters"/><Button type="submit" variant="outline-primary">Search</Button></div></Form.Group>
        </Form>
        {relationshipLoading && <div className="py-2"><Spinner size="sm" className="me-2"/>Searching...</div>}
        <div className="list-group mt-3">{relationshipResults.map((item) => <button type="button" key={item.id} className={`list-group-item list-group-item-action ${relationshipSelection === item.id ? "active" : ""}`} onClick={() => setRelationshipSelection(item.id)}><strong>{item.physicalId ?? "—"}</strong> · {item.name}</button>)}{!relationshipLoading && relationshipSearch.trim().length >= 2 && relationshipResults.length === 0 && <div className="list-group-item text-muted">No matching records.</div>}</div>
      </Modal.Body>
      <Modal.Footer><Button variant="outline-secondary" onClick={() => setRelationshipMode(null)} disabled={savingRelationship}>Cancel</Button><Button variant="primary" onClick={() => void saveRelationship()} disabled={savingRelationship || relationshipSelection == null}>{savingRelationship ? "Saving..." : relationshipMode === "add-file" ? "Add File" : "Move File"}</Button></Modal.Footer>
    </Modal>
  </div>;
}
